import { useCallback, useRef, useState } from 'react'

import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import useTracker from 'src/lib/js/hooks/useTracker'

import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { useCart } from 'public/components/online_ordering/CartContext'

import config from 'config'

import {
  RECAPTCHA_ACTION_GIFT_CARD_INQUIRY,
  RECAPTCHA_TOKEN_EXPIRY_BUFFER_MS,
  RECAPTCHA_TOKEN_EXPIRY_MS,
  RECAPTCHA_TOKEN_MAX_ATTEMPTS,
  ReCaptchaSource
} from './constants'
import { generateReCaptchaTokenWithRetries } from './generateReCaptchaToken'

type TokenCache = {
  token: string | undefined
  generatedAt: number
}

/**
 * Manages reCAPTCHA token generation for 1P gift card balance inquiries.
 *
 * Call `startGeneration()` when the gift card modal opens — the token is fetched
 * in the background while the user types, so it is usually ready by the time
 * they click apply.
 *
 * Call `getToken()` at apply time. It returns immediately if the token is still
 * fresh, awaits the in-flight generation if it is still running, or regenerates
 * if the token has expired (>2 min). `isAwaitingToken` is true only during that
 * last wait, so the apply button shows a loading state only when the user would
 * otherwise be blocked.
 *
 * Fail-open: all paths resolve with `undefined` on captcha failure so the inquiry
 * still fires without a token. Failures are reported to Sentry and Amplitude.
 */
export const useGiftCardInquiryReCaptchaToken = () => {
  const tracker = useTracker()
  const { cartGuid } = useCart()
  const [isAwaitingToken, setIsAwaitingToken] = useState(false)

  const cacheRef = useRef<TokenCache | null>(null)
  // Holds the in-flight promise so concurrent callers share one generation.
  const inFlightRef = useRef<Promise<string | undefined> | null>(null)

  const generate = useCallback((): Promise<string | undefined> => {
    const promise = generateReCaptchaTokenWithRetries(
      config.reCaptchaEnterprise.siteKey,
      RECAPTCHA_ACTION_GIFT_CARD_INQUIRY,
      (lastError, durationMs) => {
        const failureData = {
          source: ReCaptchaSource.GIFT_CARD_INQUIRY_1P,
          action: RECAPTCHA_ACTION_GIFT_CARD_INQUIRY,
          attempts: RECAPTCHA_TOKEN_MAX_ATTEMPTS,
          message: lastError?.message,
          durationMs,
          cartGuid
        }
        reportErrorMessageWithData(
          'Error generating reCAPTCHA token for gift card inquiry',
          failureData
        )
        tracker.track(
          TrackingEvents.RECAPTCHA_TOKEN_GENERATION_FAILED,
          failureData
        )
      }
    ).then((token) => {
      cacheRef.current = { token, generatedAt: Date.now() }
      inFlightRef.current = null
      return token
    })

    inFlightRef.current = promise
    return promise
  }, [cartGuid, tracker])

  const startGeneration = useCallback(() => {
    cacheRef.current = null
    inFlightRef.current = null
    generate()
  }, [generate])

  const getToken = useCallback(async (): Promise<string | undefined> => {
    // Fresh cached token — no wait needed.
    const cached = cacheRef.current
    if (
      cached &&
      Date.now() - cached.generatedAt <
        RECAPTCHA_TOKEN_EXPIRY_MS - RECAPTCHA_TOKEN_EXPIRY_BUFFER_MS
    ) {
      return cached.token
    }

    // Token is stale or missing — need to (re)generate. Show loading state.
    setIsAwaitingToken(true)
    try {
      if (inFlightRef.current) {
        // Pre-generation is still running — await it rather than starting a second.
        return await inFlightRef.current
      }
      return await generate()
    } finally {
      setIsAwaitingToken(false)
    }
  }, [generate])

  return { startGeneration, getToken, isAwaitingToken }
}
